/*
 * NovaPep Explore The Range — tab switching.
 *
 * All collection panels are server-rendered. Switching a tab only toggles the
 * `hidden` attribute, so there is no network request and no loading state.
 * Product images are `loading="lazy"`, so images inside hidden panels are never
 * fetched until their panel is revealed.
 */

if (!customElements.get('novapep-range-tabs')) {
  class NovapepRangeTabs extends HTMLElement {
    constructor() {
      super();
      this.onBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (this.tabs.length === 0) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab));
        tab.addEventListener('keydown', (event) => this.onKeydown(event));
      });

      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    panelFor(tab) {
      const id = tab.getAttribute('aria-controls');
      return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    }

    activate(tab, { focus = false } = {}) {
      if (!tab || tab.getAttribute('aria-selected') === 'true') return;

      this.tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
        item.setAttribute('tabindex', selected ? '0' : '-1');

        const panel = this.panelFor(item);
        if (panel) panel.hidden = !selected;
      });

      if (focus) tab.focus();
    }

    onKeydown(event) {
      const index = this.tabs.indexOf(event.currentTarget);
      if (index === -1) return;

      let next = null;

      switch (event.key) {
        case 'ArrowRight':
          next = this.tabs[(index + 1) % this.tabs.length];
          break;
        case 'ArrowLeft':
          next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
          break;
        case 'Home':
          next = this.tabs[0];
          break;
        case 'End':
          next = this.tabs[this.tabs.length - 1];
          break;
        default:
          return;
      }

      event.preventDefault();
      this.activate(next, { focus: true });
    }

    // Theme editor: selecting a tab block in the sidebar reveals that tab.
    onBlockSelect(event) {
      const tab = this.querySelector(`[data-block-id="${CSS.escape(event.detail.blockId)}"]`);
      if (tab) this.activate(tab);
    }
  }

  customElements.define('novapep-range-tabs', NovapepRangeTabs);
}
